import { roundDecimal } from '@autosign/utils'
import { useClickAway } from '@uidotdev/usehooks'
import { Color } from 'database'
import { useState } from 'react'
import { Rnd } from 'react-rnd'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from 'ui'
import { PDField, usePDFStore } from '../../../stores/pdf.store'
import { findBoundaryFromField } from '../../../utils/findBoundaryFromField'
import { getColorHex } from '../../../utils/getColorHex'
import { buildPagePortalId } from '../../PDFViewer'
import { RecipientItemMark, mapRecipientLabel } from '../../Recipient'
import { TunnelRat } from '../../TunnelRat'
import {
  DuplicateFieldButton,
  FIELDS_SETTINGS_PANEL_ID,
} from '../FieldSettingsPanel'
import { FieldItem, FieldItemIcon, FieldItemLabel } from './FieldItem'

export const ResizeHandler = ({
  focus,
  dir,
  color,
}: {
  focus: boolean
  dir:
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
  color: Color
}): React.ReactElement<any> => {
  return (
    <div className='w-full h-full relative'>
      <div className='w-full h-full absolute flex items-center justify-center'>
        <button
          style={{
            borderColor: getColorHex(color, '800'),
          }}
          className={cn(
            'border rounded-full bg-white w-[10px] h-[10px] z-[10000] transition-all',
            {
              'opacity-0': !focus,
              'cursor-n-resize': dir === 'top',
              'cursor-s-resize': dir === 'bottom',
              'cursor-e-resize': dir === 'right',
              'cursor-w-resize': dir === 'left',
              'cursor-nw-resize': dir === 'topLeft',
              'cursor-ne-resize': dir === 'topRight',
              'cursor-sw-resize': dir === 'bottomLeft',
              'cursor-se-resize': dir === 'bottomRight',
            }
          )}
        />
      </div>
    </div>
  )
}

export const EditableField = ({
  field,
  index,
}: {
  field: PDField
  index: number
}) => {
  const pageBoundary = usePDFStore(state => findBoundaryFromField(state, field))
  const updateField = usePDFStore(state => state.updateField)
  const updateStore = usePDFStore(state => state.update)
  const blurCurrentEditingField = usePDFStore(
    state => state.blurCurrentEditingField
  )
  const focusedBlock = usePDFStore(state => state.focusedBlock)
  const currentEditingFieldId = usePDFStore(
    state => state.currentEditingFieldId
  )

  const focus = currentEditingFieldId === field.id

  const [isDragging, setIsDragging] = useState(false)

  const awayRef = useClickAway(ev => {
    if (currentEditingFieldId !== field.id) return

    const fieldsSettingsPanel = window.document.getElementById(
      FIELDS_SETTINGS_PANEL_ID
    )!

    if (
      fieldsSettingsPanel?.contains(ev.target!) ||
      ev.target?.innerHTML?.includes('<body') ||
      ev.target?.innerHTML?.includes(`radix`) ||
      ev.target?.outerHTML?.includes(`radix`)
    )
      return

    if (currentEditingFieldId === field.id) {
      blurCurrentEditingField()
    }
  })

  const recipientIndex = usePDFStore(
    state => state.recipients.findIndex(r => r.id === field.recipient_id)!
  )

  const recipient = usePDFStore(state => state.recipients[recipientIndex])

  const recipients = usePDFStore(state => state.recipients)

  const color = recipient.color

  if (!pageBoundary) return null

  const tunnelId = buildPagePortalId(field.document_id, field.page)

  return (
    <TunnelRat tunnelId={tunnelId}>
      {({ element }) => {
        const height = roundDecimal((field.height / 100) * pageBoundary.height)

        return (
          <Rnd
            className={cn({
              '!cursor-none': !!focusedBlock,
            })}
            enableResizing={
              !!focusedBlock
                ? false
                : {
                    bottom: false,
                    top: false,
                    bottomRight: true,
                    bottomLeft: true,
                    topLeft: true,
                    topRight: true,
                    left: false,
                    right: false,
                  }
            }
            disableDragging={!!focusedBlock}
            minHeight={10}
            onDragStart={() => setIsDragging(true)}
            resizeHandleClasses={{
              bottomRight: cn({
                'opacity-0': isDragging,
              }),
              bottomLeft: cn({
                'opacity-0': isDragging,
              }),
              bottom: cn({
                'opacity-0': isDragging,
              }),
              left: cn({
                'opacity-0': isDragging,
              }),
              right: cn({
                'opacity-0': isDragging,
              }),
              top: cn({
                'opacity-0': isDragging,
              }),
              topLeft: cn({
                'opacity-0': isDragging,
              }),
              topRight: cn({
                'opacity-0': isDragging,
              }),
            }}
            resizeHandleComponent={{
              bottomRight: (
                <ResizeHandler color={color} dir='bottomRight' focus={focus} />
              ),
              bottomLeft: (
                <ResizeHandler color={color} dir='bottomLeft' focus={focus} />
              ),
              topLeft: (
                <ResizeHandler color={color} dir='topLeft' focus={focus} />
              ),
              topRight: (
                <ResizeHandler color={color} dir='topRight' focus={focus} />
              ),
            }}
            dragAxis='both'
            bounds={element?.parentElement! || 'parent'}
            onResizeStop={(e, direction, ref, delta, position) => {
              const width = +ref.style.width.replace('px', '')
              const height = +ref.style.height.replace('px', '')

              updateField(field.id, {
                positionX: roundDecimal(
                  (position.x / pageBoundary.width) * 100
                ),
                positionY: roundDecimal(
                  (position.y / pageBoundary.height) * 100
                ),
                width: roundDecimal((width / pageBoundary.width) * 100),
                height: roundDecimal((height / pageBoundary.height) * 100),
              })
            }}
            onDragStop={(e, d) => {
              setIsDragging(false)

              updateField(field.id, {
                positionX: roundDecimal((d.x / pageBoundary.width) * 100),
                positionY: roundDecimal((d.y / pageBoundary.height) * 100),
              })
            }}
            size={{
              width: roundDecimal((field.width / 100) * pageBoundary.width),
              height,
            }}
            position={{
              y: roundDecimal((field.positionY / 100) * pageBoundary.height),
              x: roundDecimal((field.positionX / 100) * pageBoundary.width),
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <FieldItem
                    fontColor={field.font_size}
                    fontSize={field.font_size}
                    fontFamily={field.font_family}
                    style={{
                      color: color === 'black' ? 'white' : field.font_color,
                      background: getColorHex(color),
                      '--tw-ring-color': getColorHex(color, '800'),
                    }}
                    ref={awayRef}
                    className={cn('!w-full !h-full', {
                      'ring-2': focus && !isDragging,
                      'pointer-events-none cursor-none': !!focusedBlock,
                    })}
                    onClick={() =>
                      updateStore('currentEditingFieldId', field.id)
                    }
                  >
                    <FieldItemLabel
                      className={cn({
                        'font-bold': field.bold,
                        italic: field.italic,
                        underline: field.underline,
                      })}
                    >
                      <FieldItemIcon type={field.type} />
                      {field!.label}
                    </FieldItemLabel>
                  </FieldItem>
                </TooltipTrigger>
                <TooltipContent>
                  <div className='space-y-1 text-center'>
                    <div className='flex flex-row items-center space-x-1 text-xs text-muted-foreground'>
                      <RecipientItemMark
                        className='w-3 h-3'
                        color={recipient.color}
                      />
                      <p>
                        {mapRecipientLabel(
                          recipient,
                          recipientIndex,
                          recipients
                        )}
                      </p>
                    </div>
                    <p className='flex items-center space-x-1 w-full justify-center'>
                      <span className='mr-2'>{field.label}</span>
                      <DuplicateFieldButton fieldId={field.id} minimized />
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Rnd>
        )
      }}
    </TunnelRat>
  )
}
